import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { hideModal } from "../../data/modal";
import { updateTenant as updateTenantAction, createTenant as createTenantAction } from "../../data/tenants";
import { Tenant } from "../../types/Tenant";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Switch,
  TextField,
  FormControlLabel,
} from "@mui/material";
import { Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";

interface GenesisTenantFormProps {
  mode: 'create' | 'edit';
  type: string;
  item?: Tenant;
  onSave?: (data: Partial<Tenant>) => void;
}

const GenesisTenantForm = ({ mode, item, onSave }: GenesisTenantFormProps) => {
  const dispatch = useDispatch();
  const isCreate = mode === 'create';

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = useForm<Partial<Tenant>>({
    defaultValues: isCreate
      ? {
          name: '',
          isActive: true,
        }
      : {
          name: item?.name || '',
          isActive: item?.isActive ?? true,
        },
  });

  useEffect(() => {
    if (!isCreate && item) {
      reset({
        name: item.name || '',
        isActive: item.isActive ?? true,
      });
    }
  }, [item, isCreate, reset]);

  const handleClose = () => {
    dispatch(hideModal());
  };

  const onSubmit = (data: Partial<Tenant>) => {
    const formData = {
      ...data,
    };

    if (isCreate) {
      dispatch(createTenantAction(formData, () => {
        dispatch(hideModal());
        if (onSave) onSave(formData);
      }) as any);
    } else {
      // For edit, we need to include the @id
      const updateData = {
        ...formData,
        "@id": item?.["@id"] || item?.id,
      };
      dispatch(updateTenantAction(updateData) as any);
      if (onSave) onSave(updateData);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>
        {isCreate ? 'Create Tenant' : 'Edit Tenant'}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Name *"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value || false}
                      onChange={(e, checked) => field.onChange(checked)}
                      disabled={isSubmitting}
                    />
                  }
                  label="Active"
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default GenesisTenantForm;
